import { useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { BROADCASTED_WAITLIST, CONNECT, GOT_WAITLIST_NOTIFICATION } from 'constants/eventConstants';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  buildBroadcastedWaitlistAction,
  buildWaitlistNotificationAction
} from 'listeners/waitlistListeners';
import { replaceWaitlistEntries } from 'reducers/waitlist';
import { fetchMyWaitlists } from 'services/waitlistService';
import type { RootState } from 'store';
import { decompressData } from 'utils/functions';
import { suppressLeftWaitlistReentries } from 'utils/recentlyLeftWaitlists';
import {
  summarizeForWaitlistTimerLog,
  summarizeWaitlistEntryForTimerLog,
  summarizeWaitlistTimerFieldsForLog,
  waitlistTimerLog
} from 'utils/waitlistTimerDebug';

type SocketLike = {
  connected?: boolean;
  on: (event: string, handler: (data?: unknown) => void) => void;
  off: (event: string, handler?: (data?: unknown) => void) => void;
};

const readStoredPlayerGuid = (): string => {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem('playerGUID') || '';
};

const useWaitlistSocketSync = (socket: SocketLike | null | undefined) => {
  const dispatch = useAppDispatch();
  // Stable reference, and deliberately NOT a subscription — see the partial
  // failure branch in syncMyWaitlists for why this must not re-render on every
  // waitlist broadcast.
  const store = useStore<RootState>();
  const playerGuid = useAppSelector((state) => state.user.playerGUID);
  const playerGuidRef = useRef(playerGuid || readStoredPlayerGuid());
  const broadcastVersionRef = useRef(0);

  useEffect(() => {
    playerGuidRef.current = playerGuid || readStoredPlayerGuid();
  }, [playerGuid]);

  useEffect(() => {
    if (!socket) return;

    const currentPlayerGuid = () => playerGuidRef.current || readStoredPlayerGuid();

    const syncMyWaitlists = async () => {
      const guid = currentPlayerGuid();
      if (!guid) {
        waitlistTimerLog('fetch:skip-no-player-guid');
        return;
      }

      const versionAtStart = broadcastVersionRef.current;
      waitlistTimerLog('fetch:start', { guid, versionAtStart });
      const gameTypes = ['texas', 'omaha'] as const;
      const results = await Promise.allSettled(
        gameTypes.map((gameType) => fetchMyWaitlists({ gameType, playerGuid: guid }))
      );

      if (versionAtStart !== broadcastVersionRef.current) {
        waitlistTimerLog('fetch:discard-stale', {
          guid,
          versionAtStart,
          currentVersion: broadcastVersionRef.current
        });
        return;
      }

      const fetchedEntries = results.flatMap((result) =>
        result.status === 'fulfilled' ? result.value : []
      );
      const failedFetches = results.flatMap((result, index) =>
        result.status === 'rejected'
          ? [
              {
                gameType: gameTypes[index],
                reason: result.reason instanceof Error ? result.reason.message : result.reason
              }
            ]
          : []
      );
      if (failedFetches.length > 0) {
        waitlistTimerLog('fetch:errors', {
          guid,
          failedFetches
        });
      }
      // Every fetch failed — we learned NOTHING about this player's spots, so
      // do not let that be written down as "on no waitlists".
      // `replaceWaitlistEntries` clears entriesByGameId/mySpotByGameId wholesale
      // before repopulating, so dispatching an all-empty result here erases the
      // player's own queued spot. That used to be masked: an expired-token 401
      // tore the session down and hard-reloaded the SPA, so the wiped store went
      // with it. Now that an expired-but-refreshable session correctly stays up
      // and refreshes, the player would instead sit looking at a waitlist they
      // appear to have left until the next periodic broadcast repaired it.
      // Partial failures still dispatch, exactly as before — one game type
      // reporting is real information.
      if (failedFetches.length === gameTypes.length) {
        waitlistTimerLog('fetch:skip-all-failed', {
          guid,
          failedFetches
        });
        return;
      }

      // A PARTIAL failure is not a licence to replace everything either.
      // replaceWaitlistEntries clears entriesByGameId/mySpotByGameId wholesale
      // before repopulating, so dispatching only the game types that answered
      // erases the spots of the game type that did not — the same data loss the
      // all-failed guard above prevents, just narrower. A texas result is
      // authoritative about texas and says nothing whatsoever about omaha, so
      // carry omaha's current entries forward untouched rather than inventing
      // an emptiness we never observed.
      //
      // Read through the store rather than a selector: subscribing to
      // entriesByGameId would re-render this hook's host (App) on every periodic
      // broadcast, since replaceWaitlistEntries assigns a fresh object each time.
      const failedTypes = new Set<string>(failedFetches.map((failure) => failure.gameType));
      const preservedEntries = Object.values(store.getState().waitlist.entriesByGameId)
        .flat()
        .filter((entry) => failedTypes.has(entry.gameType));

      // Drop our own spot for any game we just left — the fetch can race
      // ahead of the server-side DELETE and would otherwise re-add it. Applied
      // to the MERGED array so the carry-forward above cannot resurrect a spot
      // the player has explicitly left in a game type whose fetch failed.
      const entries = suppressLeftWaitlistReentries([...fetchedEntries, ...preservedEntries], guid);

      waitlistTimerLog('fetch:dispatch', {
        guid,
        entryCount: entries.length,
        entries: entries.map(summarizeWaitlistEntryForTimerLog),
        preservedTypes: Array.from(failedTypes),
        preservedCount: preservedEntries.length
      });
      dispatch(replaceWaitlistEntries({ playerGuid: guid, entries }));
    };

    const handleBroadcast = (data?: unknown) => {
      broadcastVersionRef.current += 1;
      const version = broadcastVersionRef.current;
      const guid = currentPlayerGuid();
      waitlistTimerLog('broadcast:received', {
        version,
        guid,
        raw: summarizeForWaitlistTimerLog(data)
      });
      const decoded = decompressData(data);
      waitlistTimerLog('broadcast:decoded', {
        version,
        guid,
        decoded: summarizeForWaitlistTimerLog(decoded)
      });
      const action = buildBroadcastedWaitlistAction(decoded, guid);
      // A periodic broadcast can still list a spot the player just left (the
      // engine hasn't processed the DELETE yet). Suppress our own re-added
      // entry so the lobby "Join Waitlist" banner doesn't get re-hidden.
      action.payload.entries = suppressLeftWaitlistReentries(action.payload.entries, guid);
      const myEntries = action.payload.entries.filter((entry) => entry.playerGuid === guid);
      const myTimerEntries = myEntries.filter(
        (entry) => entry.waitForAcceptStartTime && entry.waitlistTimeOut > 0
      );
      waitlistTimerLog('broadcast:dispatch', {
        version,
        guid,
        entryCount: action.payload.entries.length,
        myEntryCount: myEntries.length,
        myEntries: myEntries.map(summarizeWaitlistEntryForTimerLog),
        myFirstEntry: summarizeWaitlistEntryForTimerLog(myEntries[0]),
        myTimerEntryCount: myTimerEntries.length,
        myFirstTimerEntry: summarizeWaitlistEntryForTimerLog(myTimerEntries[0]),
        activeOffers: action.payload.entries
          .filter((entry) => entry.yourTurn && entry.availableSeats > 0)
          .map(summarizeWaitlistEntryForTimerLog)
      });
      waitlistTimerLog('broadcast:timer-fields:mine', {
        version,
        guid,
        myEntryCount: myEntries.length,
        myEntries: myEntries.map(summarizeWaitlistTimerFieldsForLog),
        myTimerEntryCount: myTimerEntries.length,
        myTimerEntries: myTimerEntries.map(summarizeWaitlistTimerFieldsForLog)
      });
      dispatch(action);
    };

    const handleNotification = (data?: unknown) => {
      waitlistTimerLog('notification:received', {
        raw: summarizeForWaitlistTimerLog(data)
      });
      const decoded = decompressData(data);
      waitlistTimerLog('notification:decoded', {
        decoded: summarizeForWaitlistTimerLog(decoded)
      });
      const action = buildWaitlistNotificationAction(decoded);
      waitlistTimerLog(action ? 'notification:dispatch' : 'notification:ignored', {
        payload: action?.payload ?? null
      });
      if (action) dispatch(action);
    };

    waitlistTimerLog('socket:subscribe', {
      events: [BROADCASTED_WAITLIST, GOT_WAITLIST_NOTIFICATION, CONNECT],
      connected: Boolean(socket.connected)
    });
    socket.on(BROADCASTED_WAITLIST, handleBroadcast);
    socket.on(GOT_WAITLIST_NOTIFICATION, handleNotification);
    socket.on(CONNECT, syncMyWaitlists);

    if (socket.connected) {
      syncMyWaitlists();
    }

    return () => {
      waitlistTimerLog('socket:unsubscribe');
      socket.off(BROADCASTED_WAITLIST, handleBroadcast);
      socket.off(GOT_WAITLIST_NOTIFICATION, handleNotification);
      socket.off(CONNECT, syncMyWaitlists);
    };
  }, [socket, dispatch, store]);
};

export default useWaitlistSocketSync;
